import React, { useEffect, useState } from "react";
import { CancelBookingPage } from "./CancelBokkingPage";
import { getReservation } from "../api/services/reservationService";
import { useNavigate } from "react-router-dom";
import {
  CircleCheck,
  Car,
  MapPin,
  Calendar,
  Clock,
  Phone,
  Ticket,
  Trash2,
  Search,
} from "lucide-react";

export default function ManageBookingPage() {
  const [form, setForm] = useState({
    email: "",
    reservationNumber: "",
    reservationStatus: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState("ENTER"); // ENTER | CHOICE | VIEW | CANCEL
  const [booking, setBooking] = useState(null);
  const navigate = useNavigate();

  /* ===============================
     Helpers
  ================================ */

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isValidReservationNumber = (value) => /^[a-zA-Z0-9]+$/.test(value); // no special characters

  /* ===============================
     Handlers
  ================================ */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({ ...prev, [name]: value }));
    setError(""); // clear error while typing
  };

  const fetchBooking = async ({ email, reservationNumber }) => {
    setLoading(true);
    setError("");

    try {
      const response = await getReservation({
        emailId: email,
        reservationNumber: reservationNumber,
      });

      // If backend succeeds but returns no data or "failed" status
      if (response && response.reservation_no) {
        setBooking(response);
        setStep("CHOICE");
        return response;
      }

      setError("Incorrect reservation credentials");
      return null;
    } catch (err) {
      const status = err?.response?.status;
      const backendMessage = err?.response?.data?.message;

      // if (status === 400) {
      //   setStep("CHOICE");
      //   const booking = {
      //     booking_status: "CANCELLED",
      //     reservation_no: reservationNumber,
      //   };
      //   setBooking(booking);
      //   return booking;
      // }

      // Handle 500 Internal Server Error or 404/401
      if (
        status === 500 ||
        status === 404 ||
        status === 401 ||
        status === 400
      ) {
        setError(backendMessage || "Incorrect reservation credentials");
      } else {
        // Fallback for network errors or other issues
        setError(
          backendMessage || "Something went wrong. Please try again later",
        );
      }

      console.error("Fetch Error:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const { email, reservationNumber } = form;

    /* ---------- Frontend validation ---------- */
    if (!email || !reservationNumber) {
      setError("Please enter both email and reservation number.");
      return;
    }

    if (!isValidEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (!isValidReservationNumber(reservationNumber)) {
      setError("Incorrect Reservation number.");
      return;
    }

    /* ---------- Fetch booking ---------- */
    await fetchBooking({
      email,
      reservationNumber,
    });
  };

  const HeroSection = (
    <div
      className="w-full pt-20 pb-20 mt-16"
      style={{
        background:
          "linear-gradient(135deg, #14222d 0%, #14222d 45%, #405759 60%, #587471 78%, #90bda8 100%)",
      }}
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="inline-flex items-center gap-2 border border-white/20 bg-white/5 rounded-full px-3 py-1 mb-6">
          <Ticket size={13} className="text-white" />
          <span className="font-heading text-white/80 text-xs font-semibold uppercase">
            My Trips
          </span>
        </div>
        <h1 className="font-heading text-5xl font-black text-white mb-4">
          Manage your booking
        </h1>
        <p className="font-heading text-white/70 text-base max-w-xl">
          View your voucher, update details, or cancel a reservation — all in
          one place, secured with OTP verification.
        </p>
      </div>
    </div>
  );

  /* ===============================
     UI
  ================================ */
  if (step === "ENTER") {
    return (
      <div className="min-h-screen bg-white">
        {/* Hero */}
        {HeroSection}

        {/* Form Card */}
        <div className="px-6">
          <div className="max-w-3xl mx-auto -mt-0 py-12">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-8">
              {/* Error */}
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email */}
                <div>
                  <label className="block text-xs font-bold font-heading text-primary uppercase tracking-widest mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    className="font-body w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                  />
                </div>

                {/* Reservation Number */}
                <div>
                  <label className="block text-xs font-bold font-heading text-primary uppercase tracking-widest mb-2">
                    Reservation Number
                  </label>
                  <input
                    type="text"
                    name="reservationNumber"
                    value={form.reservationNumber}
                    onChange={handleChange}
                    placeholder="Enter reservation number"
                    className="font-body w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                  />
                </div>

                {/* Button */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-primary hover:bg-primary-hover text-white py-3.5 rounded-xl font-semibold text-base flex items-center justify-center gap-2 transition disabled:opacity-60"
                  >
                    <Search size={18} className="text-white" />
                    {loading ? "Finding booking..." : "Find Booking"}
                  </button>
                </div>

                {/* Helper text */}
                <p className="font-body text-center text-sm text-gray-500">
                  Lost your reservation number? Check your confirmation email or{" "}
                  <a
                    href="/support"
                    className="font-body text-primary font-medium hover:underline"
                  >
                    contact support
                  </a>
                  .
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const canCancelBooking = (pickupDateTime) => {
    if (!pickupDateTime) return false;

    const pickup = new Date(
      pickupDateTime.replace(" UTC", "").replace(" - ", " "),
    ).getTime();

    if (isNaN(pickup)) return false;

    const now = Date.now();
    const diffHours = (pickup - now) / (1000 * 60 * 60);

    return diffHours >= 48;
  };

  if (step === "CHOICE") {
    const isCancelled = booking?.booking_status?.toUpperCase() === "CANCELLED";
    const canCancel = canCancelBooking(booking?.itinerary?.pickup?.date_time);

    const formatDate = (dt) => {
      if (!dt) return "N/A";
      return new Date(dt).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };

    const formatTime = (dt) => {
      if (!dt) return "N/A";
      return new Date(dt).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    };

    return (
      <div className="min-h-screen bg-gray-50">
        {HeroSection}

        {/* Top info bar */}
        <div className="max-w-6xl mx-auto px-6 mt-12 mb-2">
          <div className="bg-white border border-primary rounded-2xl md:rounded-2xl px-6 py-3 flex flex-col md:flex-row md:items-center md:justify-between shadow-sm gap-2 md:gap-0">
            <p className="text-xs text-gray-500 font-heading">
              Showing booking{" "}
              <span className="font-heading text-gray-800 font-bold">
                #{booking?.reservation_no}
              </span>
              {" · "}
              <span>{booking?.customer?.email}</span>
            </p>
            <button
              onClick={() => setStep("ENTER")}
              className="text-xs font-semibold text-primary hover:underline font-heading text-left"
            >
              Look up another booking
            </button>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 mt-6 mb-10">
          {isCancelled ? (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-6">
              <h3 className="text-lg font-bold font-heading">
                This booking has been cancelled
              </h3>
              <p className="text-sm mt-2 font-body">
                No further actions are available for this reservation.
              </p>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-6">
              {/* ── Left: Booking Summary Card ── */}
              <div className="flex-[2] bg-white border border-gray-200 rounded-2xl p-6">
                {/* Header row */}
                <div className="flex flex-col gap-1 md:flex-row md:items-center mt-4 md:justify-between md:gap-0 mb-5">
                  <div className="flex items-center gap-3">
                    <div className="font-heading inline-flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-bold tracking-widest px-3 py-1 rounded-full">
                      <CircleCheck size={13} />
                      {booking?.booking_status}
                    </div>
                    <span className="font-heading font-bold text-gray-800 text-sm">
                      Booking #{booking?.reservation_no}
                    </span>
                  </div>
                  <span className="font-heading text-sm text-gray-400">
                    {booking?.rental_days}-day rental ·{" "}
                    {booking?.itinerary?.pickup?.location_name?.split(" ")[0]}
                  </span>
                </div>

                {/* Car info */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Car size={28} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-heading font-black text-2xl text-gray-900">
                      {booking?.car?.make_and_model}
                    </p>
                    <p className="font-heading text-sm text-gray-400 font-body">
                      {booking?.car?.car_category} · or similar
                    </p>
                  </div>
                </div>

                {/* Pickup & Dropoff */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Pickup */}
                  <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
                    <div className="flex items-center gap-1.5 text-primary text-xs font-bold uppercase tracking-widest mb-2">
                      <MapPin size={12} />
                      Pick-up
                    </div>
                    <p className="font-heading font-bold text-gray-900 text-sm mb-2">
                      {booking?.itinerary?.pickup?.location_name} (
                      {booking?.itinerary?.pickup?.location_code})
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500 font-body">
                      <span className="flex items-center gap-1">
                        <Calendar size={11} />
                        {formatDate(booking?.itinerary?.pickup?.date_time)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={11} />
                        {formatTime(booking?.itinerary?.pickup?.date_time)}
                      </span>
                    </div>
                  </div>

                  {/* Dropoff */}
                  <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
                    <div className="flex items-center gap-1.5 text-primary text-xs font-bold uppercase tracking-widest mb-2">
                      <MapPin size={12} />
                      Drop-off
                    </div>
                    <p className="font-heading font-bold text-gray-900 text-sm mb-2">
                      {booking?.itinerary?.drop_off?.location_name} (
                      {booking?.itinerary?.drop_off?.location_code})
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500 font-body">
                      <span className="flex items-center gap-1">
                        <Calendar size={11} />
                        {formatDate(booking?.itinerary?.drop_off?.date_time)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={11} />
                        {formatTime(booking?.itinerary?.drop_off?.date_time)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Right: Action Cards ── */}
              <div className="flex-1 flex flex-col gap-4">
                {/* View Booking */}
                <button
                  onClick={() => {
                    const voucherData = {
                      emailId: form.email,
                      reservationNumber: form.reservationNumber,
                    };
                    sessionStorage.setItem(
                      "voucher",
                      JSON.stringify(voucherData),
                    );
                    window.open("/rental-voucher", "_blank");
                  }}
                  className="bg-white border border-gray-200 rounded-2xl p-5 text-left hover:border-primary hover:shadow-lg transition-all duration-200"
                >
                  <div className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center mb-3">
                    <Ticket size={20} className="text-primary" />
                  </div>
                  <h3 className="font-heading font-black text-gray-900 mb-1">
                    View Booking / Voucher
                  </h3>
                  <p className="font-body text-sm text-gray-500">
                    See your itinerary, vehicle and payment summary.
                  </p>
                </button>

                {/* Cancel Booking */}
                {canCancel && (
                  <button
                    onClick={() => setStep("CANCEL")}
                    className="bg-white border border-gray-200 rounded-2xl p-5 text-left hover:border-red-400 hover:shadow-lg transition-all duration-200"
                  >
                    <div className="w-11 h-11 rounded-2xl bg-red-50 flex items-center justify-center mb-3">
                      <Trash2 size={20} className="text-red-500" />
                    </div>
                    <h3 className="font-heading font-black text-gray-900 mb-1">
                      Cancel Booking
                    </h3>
                    <p className="font-body text-sm text-gray-500">
                      Verify with OTP and cancel your reservation.
                    </p>
                  </button>
                )}

                {/* Need Help */}
                <div className="bg-white border border-gray-200 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Phone size={16} className="text-primary" />
                    <h3 className="font-heading font-black text-gray-900">
                      Need help?
                    </h3>
                  </div>
                  <p className="font-body text-sm text-gray-500 mb-2">
                    Our support team is available 24/7 for any changes.
                  </p>
                  <a
                    href="tel:+18885550199"
                    className="font-heading font-bold text-primary text-sm hover:underline"
                  >
                    +1 (888) 555–0199
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* < 48h warning */}
          {!isCancelled && !canCancel && (
            <div className="mt-6 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-xl p-4 text-sm font-body">
              This booking can no longer be cancelled as the pickup time is
              within 48 hours.
            </div>
          )}
        </div>
      </div>
    );
  }

  if (step === "VIEW") {
    return (
      <ViewBookingPage booking={booking} onBack={() => setStep("CHOICE")} />
    );
  }
  if (step === "CANCEL") {
    return (
      <CancelBookingPage
        booking={booking}
        email={form.email}
        onBack={() => setStep("CHOICE")}
        onCancelled={() =>
          fetchBooking({
            email: form.email,
            reservationNumber: form.reservationNumber,
          })
        }
      />
    );
  }
}

/* ===============================
   View Booking Page
================================ */

function ViewBookingPage({ booking, onBack }) {
  const [policyInfo, setPolicyInfo] = useState(null);
  const [loadingPolicy, setLoadingPolicy] = useState(false);

  /* ---------- Helpers ---------- */

  const formatDateTime = (isoString) => {
    if (!isoString) return "N/A";
    const date = new Date(isoString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
    }).format(amount);
  };

  /* ---------- Fetch Location Policy ---------- */

  // useEffect(() => {
  //   if (!booking) return;

  //   const fetchPolicy = async () => {
  //     try {
  //       setLoadingPolicy(true);

  //       // Using only itinerary fields from API
  //       const payload = {
  //         pickupLocation: booking.itinerary.pickup_location_code,
  //         dropoffLocation: booking.itinerary.drop_off_location_code,
  //         pickupDate: booking.itinerary.pickup_date_time,
  //         dropoffDate: booking.itinerary.drop_off_date_time,
  //         vendorCode: booking.vendor || "RO",
  //       };

  //       const response = await getLocationPolicyInfo(payload);
  //       setPolicyInfo(response);
  //     } catch (err) {
  //       console.error("Policy fetch failed", err);
  //     } finally {
  //       setLoadingPolicy(false);
  //     }
  //   };

  //   fetchPolicy();
  // }, [booking]);

  const extras = booking.customer_pricing?.extras || [];

  return (
    <div className="min-h-screen bg-gray-50 mt-5">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center gap-4">
          <button onClick={onBack} className="text-xl font-semibold">
            ←
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Booking Details
            </h1>
            <p className="text-gray-600">
              Reservation: {booking.reservation_no}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* 1. Summary with Car Image */}
        <Card title="Booking Summary">
          <div className="flex flex-col md:flex-row gap-8 items-center">
            {booking.car?.car_image && (
              <div className="w-full md:w-1/3">
                <img
                  src={booking.car.car_image}
                  alt={booking.car.make_and_model}
                  className="w-full h-auto object-contain rounded-lg border p-2 bg-white"
                />
              </div>
            )}
            <div className="flex-1 w-full">
              <InfoGrid>
                <Info
                  label="Customer Name"
                  value={`${booking.customer.first_name} ${booking.customer.last_name}`}
                />
                <Info
                  label="Vehicle"
                  value={`${booking.car.make_and_model} (${booking.car.sipp_code})`}
                />
                <Info label="Status" value={booking.booking_status} />
                <Info label="Vendor" value={booking.vendor.name} />
              </InfoGrid>
            </div>
          </div>
        </Card>

        {/* 3. Time Details */}
        <Card title="Time Details">
          <InfoGrid>
            <Info
              label="Pickup Date & Time"
              value={formatDateTime(booking.itinerary.pickup.date_time)}
            />
            <Info
              label="Dropoff Date & Time"
              value={formatDateTime(booking.itinerary.dropoff.date_time)}
            />
          </InfoGrid>
        </Card>

        {/* 4. Pickup & Dropoff Details */}
        <Card title="Pickup & Dropoff Details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 border rounded-lg p-5">
              <p className="text-sm font-semibold text-[#034872] mb-1">
                Pickup Location
              </p>
              <p className="font-medium text-gray-900">
                {booking.itinerary.pickup.location_name} (
                {booking.itinerary.pickup_location_code})
              </p>

              <p className="text-xs text-gray-500 mt-2">
                Timezone: {booking.itinerary.pickup_time_zone}
              </p>
            </div>
            <div className="bg-gray-50 border rounded-lg p-5">
              <p className="text-sm font-semibold text-[#034872] mb-1">
                Dropoff Location
              </p>
              <p className="font-medium text-gray-900">
                {booking.itinerary.drop_off.location_name} (
                {booking.itinerary.drop_off_location_code})
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Timezone: {booking.itinerary.drop_off_time_zone}
              </p>
            </div>
          </div>
        </Card>

        {/* 5. Pricing Summary */}
        <Card title="Payment Summary">
          <InfoGrid>
            <Info
              label="Total Booking Amount"
              value={formatCurrency(
                booking.customer_pricing.totals.total_amount,
                booking.customer_pricing.currency,
              )}
            />
            <Info
              label="Paid Online"
              value={formatCurrency(
                booking.customer_pricing.totals.prepaid_amount,
                booking.customer_pricing.currency,
              )}
            />
            {extras.length > 0 && (
              <Card title="Selected Extras">
                <div className="divide-y divide-gray-100">
                  {extras.map((extra, index) => (
                    <div
                      key={index}
                      className="py-3 flex justify-between items-center"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {extra.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          Quantity: {extra.quantity}
                        </p>
                      </div>
                      <p className="font-semibold text-gray-900">
                        {formatCurrency(
                          extra.amount.value,
                          extra.amount.currency,
                        )}
                      </p>
                    </div>
                  ))}
                  <div className="pt-3 mt-1 flex justify-between border-t border-gray-200">
                    <span className="font-bold">
                      Total Extras (Payable at desk)
                    </span>
                    <span className="font-bold text-[#034872]">
                      {formatCurrency(
                        booking.customer_pricing.extras_amount,
                        booking.customer_pricing.currency,
                      )}
                    </span>
                  </div>
                </div>
              </Card>
            )}
          </InfoGrid>
        </Card>
      </div>
    </div>
  );
}

// Cards
export function Card({ title, children }) {
  return (
    <div className="bg-white border rounded-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      {children}
    </div>
  );
}

function InfoGrid({ children }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{children}</div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="font-medium text-gray-900">{value || "N/A"}</p>
    </div>
  );
}

function LocationCard({ title, info }) {
  const mapLink =
    info.latitude && info.longitude
      ? `https://www.google.com/maps?q=${info.latitude},${info.longitude}`
      : null;

  return (
    <div className="bg-gray-50 border rounded-lg p-5 space-y-2">
      <p className="text-sm font-semibold text-[#034872]">{title}</p>

      <p className="font-medium text-gray-900">
        {info.locationName} ({info.locationCode})
      </p>

      <p className="text-sm text-gray-700">{info.address}</p>

      {(info.phoneNumber || info.email) && (
        <p className="text-sm text-gray-600">
          {info.phoneNumber && <>📞 {info.phoneNumber}</>}
          {info.phoneNumber && info.email && " | "}
          {info.email && <>✉️ {info.email}</>}
        </p>
      )}

      {info.timing && (
        <p className="text-sm text-gray-700">
          🕒 <strong>{info.timing.day}</strong>: {info.timing.from} –{" "}
          {info.timing.to}
        </p>
      )}

      {mapLink && (
        <a
          href={mapLink}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-blue-600 underline"
        >
          View on map
        </a>
      )}
    </div>
  );
}
